import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, EmptyState, StatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import TrustBadge from "../components/TrustBadge";
import { toast } from "sonner";
import {
  CalendarClock, CheckCircle2, Trash2, Sparkles, ArrowRight, Loader2,
  DollarSign, RefreshCw, Bell, FileWarning, AlertTriangle,
} from "lucide-react";

const TYPE_ICON = {
  payment: DollarSign,
  renewal: RefreshCw,
  termination: FileWarning,
  deliverable: CheckCircle2,
  reporting: Bell,
  insurance: AlertTriangle,
  compliance: CheckCircle2,
  notice: Bell,
  milestone: CalendarClock,
};

const STATUS_TONE = {
  pending: "warn",
  confirmed: "info",
  completed: "success",
  dismissed: "neutral",
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const ms = d.getTime() - Date.now();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

export default function ObligationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [agreements, setAgreements] = useState([]);
  const [selectedAgreement, setSelectedAgreement] = useState("");
  const [extracting, setExtracting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const [{ data: obs }, { data: ags }] = await Promise.all([
        api.get("/obligations", { params }),
        api.get("/agreements"),
      ]);
      setItems(obs);
      setAgreements(ags);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const extract = async () => {
    if (!selectedAgreement) { toast.error("Pick an executed agreement first."); return; }
    setExtracting(true);
    try {
      const { data } = await api.post(`/agreements/${selectedAgreement}/extract-obligations`);
      toast.success(`Marianne found ${data.obligations.length} obligations — please confirm each one.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const confirm = async (id) => {
    await api.post(`/obligations/${id}/confirm`);
    toast.success("Obligation confirmed. Reminders active.");
    load();
  };
  const dismiss = async (id) => {
    await api.patch(`/obligations/${id}`, { status: "dismissed" });
    toast.message("Obligation dismissed.");
    load();
  };
  const complete = async (id) => {
    await api.patch(`/obligations/${id}`, { status: "completed" });
    toast.success("Marked completed.");
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this obligation?")) return;
    await api.delete(`/obligations/${id}`);
    load();
  };

  const pending = items.filter((o) => o.status === "pending");
  const confirmed = items.filter((o) => o.status === "confirmed");
  const other = items.filter((o) => !["pending", "confirmed"].includes(o.status));

  return (
    <div data-testid="obligations-page">
      <PageHeader
        eyebrow="Les Devoirs · Obligation Management"
        title="Every promise. Every renewal. Every notice."
        description="Marianne reads your executed agreements and lifts out what you owe, when it's due, and how many days you have to act. You confirm each obligation before it goes live."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={selectedAgreement}
              onChange={(e) => setSelectedAgreement(e.target.value)}
              className="border border-[#E8E3F0] bg-white px-3 py-2 text-sm rounded-none"
              data-testid="extract-agreement-select"
            >
              <option value="">Pick an agreement to analyse…</option>
              {agreements.map((a) => (<option key={a.id} value={a.id}>{a.title}</option>))}
            </select>
            <Button
              onClick={extract}
              disabled={extracting || !selectedAgreement}
              className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]"
              data-testid="extract-obligations-btn"
            >
              {extracting ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Extracting…</> : <><Sparkles size={14} className="mr-1.5" /> Extract obligations</>}
            </Button>
          </div>
        }
      />

      {/* Status filter tabs */}
      <div className="grid sm:grid-cols-4 gap-px bg-[#E8E3F0] border border-[#E8E3F0] mb-6">
        {[
          { id: "", label: "All", count: items.length },
          { id: "pending", label: "Pending review", count: items.filter((o) => o.status === "pending").length },
          { id: "confirmed", label: "Active", count: items.filter((o) => o.status === "confirmed").length },
          { id: "completed", label: "Completed", count: items.filter((o) => o.status === "completed").length },
        ].map((s) => {
          const active = statusFilter === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setStatusFilter(s.id)}
              data-testid={`obligations-filter-${s.id || "all"}`}
              className={`p-4 text-left ${active ? "bg-[#2A0F5A] text-[#FAFAF8]" : "bg-white hover:bg-[#FAFAF8]"}`}
            >
              <div className={`label-eyebrow ${active ? "!text-[#C5A059]" : ""}`}>{s.label}</div>
              <div className="font-serif text-3xl mt-1">{s.count}</div>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="label-eyebrow">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title="No obligations tracked yet"
          description="Pick an executed agreement above and let Marianne extract the concrete duties, dates and amounts. You confirm each one before reminders start."
        />
      ) : (
        <div className="space-y-4">
          {pending.length > 0 && (
            <Section title="Pending your review" tone="warn">
              {pending.map((o) => <ObligationCard key={o.id} o={o} onConfirm={() => confirm(o.id)} onDismiss={() => dismiss(o.id)} onDelete={() => remove(o.id)} />)}
            </Section>
          )}
          {confirmed.length > 0 && (
            <Section title="Active — being tracked" tone="info">
              {confirmed.map((o) => <ObligationCard key={o.id} o={o} onComplete={() => complete(o.id)} onDelete={() => remove(o.id)} />)}
            </Section>
          )}
          {other.length > 0 && (
            <Section title="History" tone="neutral">
              {other.map((o) => <ObligationCard key={o.id} o={o} onDelete={() => remove(o.id)} />)}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, tone, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="label-eyebrow">{title}</div>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function ObligationCard({ o, onConfirm, onDismiss, onComplete, onDelete }) {
  const Icon = TYPE_ICON[o.type] || CalendarClock;
  const days = daysUntil(o.due_date);
  const urgent = days !== null && days >= 0 && days <= 14;
  return (
    <div className="border border-[#E8E3F0] bg-white p-5" data-testid={`obligation-${o.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <Icon size={18} className="text-[#7B56C9] mt-1 shrink-0" />
          <div className="min-w-0">
            <div className="label-eyebrow text-[#C5A059] mb-0.5">{o.type} · {o.counterparty || "—"}</div>
            <div className="font-serif text-xl leading-tight">{o.title}</div>
            {o.description && <div className="text-sm text-[#2A0F5A]/75 mt-1">{o.description}</div>}
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-[#4A5568]">
              {o.due_date && (
                <span className={urgent ? "text-red-700 font-medium" : ""}>Due {o.due_date}{days !== null && ` · ${days >= 0 ? days + " days" : Math.abs(days) + " days ago"}`}</span>
              )}
              {o.period_note && <span>{o.period_note}</span>}
              {o.amount != null && <span>{o.currency || ""} {o.amount.toLocaleString()}</span>}
              {o.notice_period_days && <span>{o.notice_period_days}-day notice</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge tone={STATUS_TONE[o.status] || "neutral"}>{o.status}</StatusBadge>
        </div>
      </div>

      {o.source_quote && (
        <details className="mt-3">
          <summary className="text-[11px] label-eyebrow cursor-pointer text-[#4A5568]">Source clause</summary>
          <div className="mt-2 border-l-2 border-[#7B56C9] bg-[#7B56C9]/5 px-3 py-2 text-xs italic">
            "{o.source_quote}"
          </div>
        </details>
      )}

      {o.trust && <div className="mt-3"><TrustBadge trust={o.trust} compact /></div>}

      <div className="flex flex-wrap items-center gap-2 mt-4">
        {onConfirm && (
          <Button onClick={onConfirm} size="sm" className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid={`confirm-${o.id}`}>
            <CheckCircle2 size={12} className="mr-1.5" /> Confirm & track
          </Button>
        )}
        {onComplete && (
          <Button onClick={onComplete} size="sm" variant="outline" className="rounded-none border-[#E8E3F0]" data-testid={`complete-${o.id}`}>
            Mark completed
          </Button>
        )}
        {onDismiss && (
          <Button onClick={onDismiss} size="sm" variant="outline" className="rounded-none border-[#E8E3F0]" data-testid={`dismiss-${o.id}`}>
            Dismiss
          </Button>
        )}
        {o.source_agreement_id && (
          <Link to={`/app/agreements`} className="text-xs underline text-[#7B56C9] hover:text-[#2A0F5A]">Open agreement <ArrowRight size={10} className="inline" /></Link>
        )}
        <button onClick={onDelete} className="ml-auto text-[#4A5568] hover:text-red-700" title="Delete" data-testid={`delete-${o.id}`}>
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}
